import React, { useState, useEffect } from 'react';
import { CameraIcon, StarIcon, ShieldCheckIcon, ArrowRightIcon, StyleMarkIcon, DownloadIcon } from './icons';
import { TEMPLATE_CATEGORIES } from '../constants/templates';
import { TemplatePreview } from './TemplatePreview';
import { useAuth } from '../contexts/AuthContext';
import { OUTFIT_OPTIONS } from '../constants';
import { ChangelogModal } from './ChangelogModal';
import { inheritStyleAndNavigate, extractPortraitUserPrompt, type InheritedStyleSettings } from '../utils/showcaseStyle';
import { loadUserGallery } from '../utils/galleryCache';


interface LandingPageProps {
  onStartTrial: () => void;
  onShowTermsOfService: () => void;
  onShowPrivacyPolicy: () => void;
  onShowCommercialAct?: () => void;
  onPurchase: (plan: 'standard' | 'pro' | 'pro_max') => void;
  onStartApp?: () => void;
  onShowGallery?: () => void;
  onShowShowcase?: () => void;
}

const HeroSection: React.FC<{ onStartTrial: () => void }> = ({ onStartTrial }) => {
  const copy = (
    <>
      <span className="inline-flex rounded-full bg-gradient-to-r from-violet-500 to-pink-400 px-3 py-1 text-xs font-semibold text-white shadow-sm sm:px-4 sm:py-1.5 sm:text-sm">
        スマホの一枚が
      </span>
      <h1 className="mt-4 text-[1.85rem] font-bold leading-tight tracking-tight sm:text-4xl md:mt-5 md:text-5xl lg:text-[3.4rem]">
        <span className="text-slate-900 md:bg-gradient-to-r md:from-blue-700 md:via-indigo-600 md:to-fuchsia-500 md:bg-clip-text md:text-transparent">
          未来につながる
          <br />
          <span className="bg-gradient-to-r from-blue-700 via-indigo-600 to-fuchsia-500 bg-clip-text text-transparent">
            魅力的な一枚へ。
          </span>
        </span>
      </h1>
      <p className="mt-4 max-w-[16rem] text-[13px] leading-relaxed text-slate-600 sm:max-w-none sm:text-base md:mt-6 md:text-lg">
        スナップ写真から、証明写真・宣材写真・遺影まで。
        <br className="hidden sm:block" />
        AIがあなたの魅力を最大限に引き出します。
      </p>
    </>
  );

  const ctaCard = (
    <div className="rounded-2xl bg-white p-4 shadow-[0_18px_50px_rgba(79,70,229,0.14)] ring-1 ring-slate-100 sm:p-5">
      <p className="text-sm text-slate-800 sm:text-base">
        ログインして無料で
        <span className="mx-1 text-2xl font-bold text-pink-500 sm:text-3xl">3</span>
        回お試し
      </p>
      <button
        onClick={onStartTrial}
        className="mt-3 inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-fuchsia-500 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:from-blue-700 hover:to-fuchsia-600 sm:mt-4 sm:py-3.5 sm:text-base"
      >
        ログインして始める →
      </button>
      <a
        href="/showcase.html"
        className="mt-2 hidden w-full items-center justify-center rounded-full border-2 border-violet-500 px-6 py-3 text-base font-semibold text-violet-700 transition hover:bg-violet-50 md:inline-flex"
      >
        ショーケースを見る
      </a>
      <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-500 sm:mt-3 sm:text-xs">
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
        ログイン後すぐにご利用いただけます
      </p>
    </div>
  );

  const beforeAfter = (
    <div className="grid h-44 grid-cols-2">
      <div className="relative">
        <img
          src="/images/before-after/musician-before.jpg"
          alt="変換前のスナップ写真"
          className="h-full w-full object-cover"
        />
        <span className="absolute left-1.5 top-1.5 rounded bg-black/55 px-1.5 py-0.5 text-[9px] font-semibold text-white">
          スナップ写真
        </span>
      </div>
      <div className="relative">
        <img
          src="/images/before-after/hero-after.png"
          alt="変換後の宣材写真"
          className="h-full w-full object-cover"
        />
        <span className="absolute right-1.5 top-1.5 rounded bg-black/55 px-1.5 py-0.5 text-[9px] font-semibold text-white">
          仕上がりイメージ
        </span>
      </div>
    </div>
  );

  return (
    <section className="relative min-h-[36rem] overflow-hidden bg-white pb-10 pt-2 md:min-h-0 md:bg-gradient-to-b md:from-white md:via-indigo-50/40 md:to-white md:px-4 md:pb-32 md:pt-10">
      <div className="pointer-events-none absolute -right-24 -top-24 hidden h-80 w-80 rounded-full bg-fuchsia-200/40 blur-3xl md:block" />
      <div className="pointer-events-none absolute -left-16 top-40 hidden h-72 w-72 rounded-full bg-blue-200/40 blur-3xl md:block" />

      {/* スマホ：人物写真を右背景に配置 */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-[58%] md:hidden">
        <img
          src="/images/before-after/hero-portrait.png"
          alt=""
          className="h-full w-full object-cover object-[center_12%]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/75 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
      </div>

      <div className="relative container mx-auto grid max-w-6xl items-center md:gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <div className="relative z-10 max-w-xl px-4 pt-5 md:px-0 md:pt-0">
          {copy}
          <div className="mt-5 w-[78%] max-w-sm md:mt-8 md:w-full md:max-w-md">
            {ctaCard}
          </div>
        </div>

        {/* PC：右カラムの人物写真＋カード */}
        <div className="relative mx-auto hidden w-full max-w-lg md:block lg:max-w-none">
          <div className="relative overflow-hidden rounded-[2rem] bg-slate-100 shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
            <img
              src="/images/before-after/hero-portrait.png"
              alt="Profile Studio の仕上がりイメージ"
              className="aspect-[4/5] w-full object-cover object-[center_18%] md:aspect-[3/4]"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/15 via-transparent to-white/10" />
          </div>
          <div className="absolute -bottom-10 left-1 right-1 rounded-2xl bg-white/95 p-4 shadow-xl ring-1 ring-slate-100 backdrop-blur sm:left-auto sm:right-[-1.25rem] sm:w-[30rem]">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Before</p>
                <p className="mb-2 text-sm text-slate-500">スナップ写真</p>
                <img
                  src="/images/before-after/musician-before.jpg"
                  alt="変換前のスナップ写真"
                  className="h-40 w-full rounded-xl object-cover sm:h-44"
                />
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md">
                <ArrowRightIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-fuchsia-500">After</p>
                <p className="mb-2 text-sm text-slate-500">仕上がりイメージ（宣材写真）</p>
                <img
                  src="/images/before-after/hero-after.png"
                  alt="変換後の宣材写真"
                  className="h-40 w-full rounded-xl object-cover sm:h-44"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* スマホ：Before/After を端末フレームで右下に重ねる */}
      <div className="relative z-20 -mt-6 flex justify-end px-3 md:hidden">
        <div className="w-[48%] max-w-[200px] rounded-[1.7rem] border-[7px] border-slate-800 bg-slate-800 p-0.5 shadow-[0_18px_40px_rgba(15,23,42,0.35)]">
          <div className="relative overflow-hidden rounded-[1.15rem] bg-white">
            {beforeAfter}
            <div className="absolute left-1/2 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white">
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const UseCasesSection: React.FC = () => {
  const items = [
    { title: '証明写真・履歴書', desc: '就職・転職・各種申請に清潔感のあるきれいな証明写真に', icon: 'id' },
    { title: '宣材写真・プロフィール', desc: 'ビジネス・SNS・HP用の魅力的なプロフィールに', icon: 'profile' },
    { title: '楽器と一緒に', desc: 'ピアノやバイオリンなど楽器を持った写真も', icon: 'music' },
    { title: '衣装チェンジ', desc: 'ドレスやスーツなど衣装を自由に変更', icon: 'dress' },
    { title: '遺影・生前写真', desc: '大切な一枚を心を込めて美しく', icon: 'frame' },
    { title: 'その他いろいろ', desc: '家族写真・記念写真など様々なシーンで', icon: 'group' },
  ];

  const iconPath: Record<string, string> = {
    id: 'M7 7h10v10H7V7zm2 3h6M9 14h4',
    profile: 'M12 12a3.5 3.5 0 1 0-3.5-3.5A3.5 3.5 0 0 0 12 12zm-7 8a7 7 0 0 1 14 0',
    music: 'M9 18V6l10-2v12M9 18a3 3 0 1 1-3-3 3 3 0 0 1 3 3zm10-2a3 3 0 1 1-3-3 3 3 0 0 1 3 3z',
    dress: 'M9 4h6l2 5-3 2v9H10V11L7 9l2-5z',
    frame: 'M5 5h14v14H5V5zm3 3h8v8H8V8z',
    group: 'M8 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3zm8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3zM4 20a4 4 0 0 1 8 0m8 0a4 4 0 0 0-8 0',
  };

  return (
    <section id="use-cases" className="bg-white px-4 py-8 md:border-t md:border-slate-100 md:py-12">
      <h2 className="mb-6 flex items-center justify-center gap-3 text-sm font-semibold tracking-wide text-slate-700 md:hidden">
        <span className="h-px w-10 bg-slate-200" />
        <span className="text-fuchsia-400">◆</span>
        さまざまな用途に対応
        <span className="text-fuchsia-400">◆</span>
        <span className="h-px w-10 bg-slate-200" />
      </h2>
      <div className="container mx-auto grid max-w-6xl grid-cols-2 gap-3 md:grid-cols-3 md:gap-6 lg:grid-cols-6">
        {items.map((item) => (
          <div key={item.title} className="rounded-2xl bg-white p-4 text-center shadow-[0_8px_24px_rgba(15,23,42,0.06)] ring-1 ring-slate-100 md:rounded-none md:p-0 md:shadow-none md:ring-0">
            <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-fuchsia-50 md:mb-3 md:h-12 md:w-12">
              <svg className="h-6 w-6 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
                <path strokeLinecap="round" strokeLinejoin="round" d={iconPath[item.icon]} />
              </svg>
            </div>
            <h3 className="text-[13px] font-semibold text-slate-900 md:text-sm">{item.title}</h3>
            <p className="mt-1 hidden text-xs leading-relaxed text-slate-500 md:block">{item.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const FlowSection: React.FC = () => (
  <section id="flow" className="bg-slate-50 px-4 py-14">
    <div className="container mx-auto max-w-5xl">
      <h2 className="mb-10 text-center text-3xl font-bold text-slate-900">ご利用の流れ</h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[
          { step: '1', title: '写真を選ぶ', desc: 'スマホのスナップや自撮りをアップロードします。' },
          { step: '2', title: 'スタイルを選ぶ', desc: '背景・衣装・ポーズ、またはポートレート／音楽家モードを選びます。' },
          { step: '3', title: '生成する', desc: 'ログイン後すぐに3回まで無料でお試しできます。' },
        ].map((item) => (
          <div key={item.step} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-sm font-bold text-white">
              {item.step}
            </div>
            <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
            <p className="mt-2 text-sm text-slate-600">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const GallerySection: React.FC = () => (
  <section id="gallery" className="py-16 px-4">
    <div className="container mx-auto max-w-6xl">
      <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
        Before/After ギャラリー
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {/* 白背景のサンプル */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="aspect-video relative">
            <img 
              src="/images/before-after/studio-white.jpg" 
              alt="Before/After 白背景サンプル" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6">
            <h3 className="font-semibold text-lg mb-2">白背景</h3>
            <p className="text-slate-600 text-sm">清潔感のあるスタジオ仕上がり</p>
          </div>
        </div>

        {/* 店舗背景のサンプル */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="aspect-video relative">
            <img 
              src="/images/before-after/store-background.jpg" 
              alt="Before/After 店舗背景サンプル" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6">
            <h3 className="font-semibold text-lg mb-2">店舗背景</h3>
            <p className="text-slate-600 text-sm">自然なボケ味でプロ感アップ</p>
          </div>
        </div>

        {/* イベント照明のサンプル */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="aspect-video relative">
            <img 
              src="/images/before-after/event-lighting.jpg" 
              alt="Before/After イベント照明サンプル" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6">
            <h3 className="font-semibold text-lg mb-2">イベント照明</h3>
            <p className="text-slate-600 text-sm">華やかなイベント向け仕上がり</p>
          </div>
        </div>

        {/* 証明写真のサンプル */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="aspect-video relative">
            <img 
              src="/images/before-after/id-photo.jpg" 
              alt="Before/After 証明写真サンプル" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6">
            <h3 className="font-semibold text-lg mb-2">証明写真</h3>
            <p className="text-slate-600 text-sm">フォーマルな証明写真仕上がり</p>
          </div>
        </div>
      </div>
      <p className="text-center text-slate-600 mt-8">
        衣装着せ替え・背景差し替え・肌レタッチは自然に
      </p>
    </div>
  </section>
);

const IndustryTemplates: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { isPaidUser } = useAuth();
  const tabs = [
    { name: 'ビジネス', desc: 'プロフェッショナルで信頼感のある仕上がり' },
    { name: 'クリエイティブ', desc: '個性的で創造性を表現した仕上がり' },
    { name: 'カスタム', desc: 'オリジナルの背景を指定' }
  ];

  return (
    <section id="templates" className="py-16 px-4 bg-slate-50">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
          背景テンプレート
        </h2>
        <div className="flex justify-center mb-8">
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`px-6 py-3 mx-2 rounded-lg font-medium transition-colors ${
                activeTab === index
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>
        
            {/* テンプレートギャラリー */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {TEMPLATE_CATEGORIES[activeTab]?.templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white rounded-lg shadow-md p-3 hover:shadow-lg transition-shadow"
                >
                  <TemplatePreview template={template} className="mb-2" />
                  <h3 className="font-semibold text-slate-900 mb-1 text-sm">{template.name}</h3>
                  <p className="text-slate-600 text-xs leading-tight">{template.description}</p>
                  {template.isPremium && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        ⭐️ Premium
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
        
        {/* 無料版のみ表示 */}
        {!isPaidUser && (
          <div className="text-center mt-8">
            <p className="text-slate-600">
              有料プラン：保存時も高解像度の画像を作成することが可能です
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

const OutfitTemplates: React.FC = () => {
  // 衣装カテゴリ分け（categoryプロパティを使用）
  const businessOutfits = OUTFIT_OPTIONS.filter(outfit => outfit.category === 'business');
  const casualOutfits = OUTFIT_OPTIONS.filter(outfit => outfit.category === 'casual');
  const customOutfits = OUTFIT_OPTIONS.filter(outfit => outfit.category === 'custom');
  const { isPaidUser } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const tabs = [
    { name: 'ビジネス', outfits: businessOutfits },
    { name: 'カジュアル', outfits: casualOutfits },
    { name: 'カスタム', outfits: customOutfits }
  ];

  // 衣装カードのプレビューコンポーネント
  const OutfitCard: React.FC<{ outfit: OutfitOption }> = ({ outfit }) => {
    // 衣装画像があるかチェック
    const hasOutfitImage = outfit.outfitImage;
    console.log('OutfitCard:', outfit.id, 'hasImage:', hasOutfitImage, 'path:', outfit.outfitImage);
    
    // 衣装のカラーマップ（画像がない場合のフォールバック）
    const outfitColors: { [key: string]: { primary: string; secondary: string; accent: string } } = {
      'custom_text': { primary: '#6366f1', secondary: '#8b5cf6', accent: '#e0e7ff' },
      'custom_image': { primary: '#f97316', secondary: '#fb923c', accent: '#fed7aa' }
    };

    const colors = outfitColors[outfit.id] || { primary: '#3b82f6', secondary: '#1d4ed8', accent: '#1e40af' };

    return (
      <div className="bg-white rounded-lg shadow-md p-3 hover:shadow-lg transition-shadow">
        {/* プレビュー */}
        <div className="aspect-square rounded-lg overflow-hidden relative mb-2">
          {hasOutfitImage ? (
            // 画像がある場合は画像を表示
            <img 
              src={`/images/templates/${outfit.outfitImage}.jpg`}
              alt={outfit.label}
              className="w-full h-full object-cover"
            />
          ) : (
            // 画像がない場合はグラデーション表示
            <div 
              className="w-full h-full"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 50%, ${colors.accent} 100%)`
              }}
            >
              {/* テクスチャ効果 */}
              <div 
                className="absolute inset-0 opacity-20"
                style={{
                  background: `radial-gradient(circle at 30% 20%, ${colors.primary} 0%, transparent 50%), 
                              radial-gradient(circle at 70% 80%, ${colors.secondary} 0%, transparent 50%)`
                }}
              />
              
              {/* 中央のアイコンエリア */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <div className="w-5 h-5 bg-white bg-opacity-40 rounded-full"></div>
                </div>
              </div>
              
              {/* コーナーの装飾 */}
              <div className="absolute top-1 right-1 w-3 h-3 bg-white bg-opacity-30 rounded-full"></div>
              <div className="absolute bottom-1 left-1 w-2 h-2 bg-white bg-opacity-20 rounded-full"></div>
            </div>
          )}
        </div>
        
        {/* 衣装名 */}
        <h3 className="font-semibold text-slate-900 mb-1 text-sm">{outfit.label}</h3>
        
        {/* バッジ表示 */}
        {outfit.id === 'custom_text' && (
          <div className="mt-2">
            <span className="inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              テキスト指定
            </span>
          </div>
        )}
        {outfit.isPremium && (
          <div className="mt-2">
            <span className="inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              ⭐️ Premium
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <section id="outfits" className="py-16 px-4 bg-white">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
          衣装テンプレート
        </h2>
        <div className="flex justify-center mb-8">
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`px-6 py-3 mx-2 rounded-lg font-medium transition-colors ${
                activeTab === index
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>
        
        {/* 衣装ギャラリー */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {tabs[activeTab]?.outfits.map((outfit) => (
            <OutfitCard key={outfit.id} outfit={outfit} />
          ))}
        </div>
        
        <div className="text-center mt-8">
          <p className="text-slate-600">
            {activeTab === 0 && 'プロフェッショナルなビジネス衣装で信頼感のある印象を'}
            {activeTab === 1 && 'カジュアルな衣装で親しみやすい印象を'}
            {activeTab === 2 && 'テキストで自由に衣装を指定できます'}
          </p>
          {/* 無料版のみ表示 */}
          {!isPaidUser && (
            <p className="text-slate-600 mt-2">
              有料プラン：保存時も高解像度の画像を作成することが可能です
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const PricingSection: React.FC<{ onStartTrial: () => void; onPurchase: (plan: 'standard' | 'pro' | 'pro_max') => void }> = ({ onStartTrial, onPurchase }) => {
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '¥0',
      features: ['3枚（買い切り）', '低解像度（保存時）', '商用利用不可'],
      popular: false,
      buttonText: 'まずはログイン体験'
    },
    {
      id: 'standard',
      name: 'Standard',
      price: '¥500',
      features: ['10枚（買い切り）', '高解像度', '全機能利用可'],
      popular: true,
      buttonText: '購入する'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '¥1,000',
      features: ['25枚（買い切り）', '高解像度', '全機能利用可'],
      popular: false,
      buttonText: '購入する'
    },
    {
      id: 'pro_max',
      name: 'Pro Max',
      price: '¥5,000/月',
      features: ['月200枚', '高解像度', '全機能利用可'],
      popular: false,
      buttonText: '購入する'
    }
  ];

  return (
    <section id="pricing" className="py-16 px-4">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
          料金プラン
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl shadow-lg p-6 relative ${
                plan.popular ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    人気
                  </span>
                </div>
              )}
              <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
              <div className="text-3xl font-bold text-blue-600 mb-4">
                {plan.price.includes('/月（税込）') ? (
                  <>
                    {plan.price.replace('/月（税込）', '')}
                    <span className="text-lg font-normal">/月（税込）</span>
                  </>
                ) : (
                  plan.price
                )}
              </div>
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-sm text-slate-600">
                    <StarIcon className="w-4 h-4 text-green-500 mr-2" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  if (plan.id === 'free') {
                    onStartTrial();
                  } else {
                    onPurchase(plan.id as 'standard' | 'pro' | 'pro_max');
                  }
                }}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                {plan.buttonText}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TrustSection: React.FC = () => (
  <section id="trust" className="py-16 px-4 bg-slate-50">
    <div className="container mx-auto max-w-4xl text-center">
      <h2 className="text-3xl font-bold text-slate-900 mb-12">
        信頼・安心
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <ShieldCheckIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">ログインして無料で3回お試し</h3>
          <p className="text-slate-600 text-sm">高品質の仕上がりを、まずは体験版で。<br />ログイン後は3回まで無料でお試し。有料プランでは高解像度で保存できます。</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <ShieldCheckIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">データの保管と削除</h3>
          <p className="text-slate-600 text-sm">出来上がった写真は30日で削除されます。<br />入力した写真はこちらでは保存しません。<br />すべてのAI宣材写真は30日後に削除されます。<br />ギャラリーから削除のボタンをクリックするだけで、いつでもデータをすばやく削除できます。</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <ShieldCheckIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">安全・安心な決済環境</h3>
          <p className="text-slate-600 text-sm mb-3">全ての取引を業界標準の暗号化で保護。<br />FirebaseとStripeによる最高水準のセキュリティで、個人情報やカード情報を安全に保ちます。</p>
          <p className="text-slate-500 text-xs">SSL/TLS 暗号化 / Stripe連携 / Firebase Auth</p>
        </div>
      </div>
    </div>
  </section>
);

const Footer: React.FC<{ onStartTrial: () => void; onShowTermsOfService: () => void; onShowPrivacyPolicy: () => void; onShowCommercialAct?: () => void }> = ({ onStartTrial, onShowTermsOfService, onShowPrivacyPolicy, onShowCommercialAct }) => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="footer" className="bg-slate-900 text-white py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="font-bold text-lg mb-4">プロフィールスタジオ（宣材写真ジェネレーター）</h3>
            <p className="text-slate-400 text-sm mb-4">
              スマホで撮った自撮りやスナップ写真をAIによって高品質で魅力的な一枚の写真に仕上げます
            </p>
            <div className="text-slate-500 text-xs">
              <p className="font-semibold mb-2">キーワード</p>
              <p className="leading-relaxed">
                宣材写真　証明写真　フォトスタジオ　<br />
                記念写真　オーディション　遺影　<br />
                ポートレート　モデル
              </p>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">サービス</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><button onClick={onStartTrial} className="hover:text-white text-left">無料体験</button></li>
              <li><button onClick={() => scrollToSection('gallery')} className="hover:text-white text-left">ギャラリー</button></li>
              <li><button onClick={() => scrollToSection('templates')} className="hover:text-white text-left">背景テンプレート</button></li>
              <li><button onClick={() => scrollToSection('outfits')} className="hover:text-white text-left">衣装テンプレート</button></li>
              <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white text-left">料金プラン</button></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">サポート</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><button onClick={onShowTermsOfService} className="hover:text-white text-left">利用規約</button></li>
              <li><button onClick={onShowPrivacyPolicy} className="hover:text-white text-left">プライバシーポリシー</button></li>
              {onShowCommercialAct && (
                <li><button onClick={onShowCommercialAct} className="hover:text-white text-left">特定商取引法に基づく表記</button></li>
              )}
              <li><a href="mailto:support@profile-studio.online?subject=お問い合わせ&body=お問い合わせ内容をご記入ください。" className="hover:text-white">お問い合わせ</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">会社情報</h4>
            <p className="text-slate-400 text-sm">
              プロフィールスタジオ<br />
              © 2025 All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ログイン後のエンゲージメント用LP
const LoggedInLandingPage: React.FC<LandingPageProps> = ({ onStartTrial, onShowTermsOfService, onShowPrivacyPolicy, onPurchase, onStartApp, onShowGallery, onShowShowcase }) => {
  const { user, userPlan, isPaidUser, isAnonymous, refreshUserPlan } = useAuth();
  const [recentImages, setRecentImages] = useState<any[]>([]);
  const [showChangelog, setShowChangelog] = useState(false);
  
  
  // userPlan?.creditsRestを直接使用（計算不要）
  const remainingCredits = userPlan?.creditsRest || 0;
  
  // デバッグ用：クレジット情報をコンソールに出力
  console.log('LoggedInLandingPage Credit Debug:', {
    isPaidUser,
    userPlan: userPlan?.plan,
    creditsUsed: userPlan?.creditsUsed,
    creditsRest: userPlan?.creditsRest,
    remainingCredits,
    fullUserPlan: userPlan
  });

  useEffect(() => {
    const loadRecentImages = async () => {
      if (!user) return;
      
      try {
        const allImages = await loadUserGallery(user.uid, {
          skipFreshDb: true,
          onCached: (cachedImages) => {
            setRecentImages(cachedImages.slice(0, 6));
          },
        });
        setRecentImages(allImages.slice(0, 6));
      } catch (error) {
        console.error('最近の画像読み込みエラー:', error);
      }
    };
    
    loadRecentImages();
  }, [user]);

  const buildSettingsFromRecentImage = (image: any): InheritedStyleSettings | null => {
    if (image.settings) {
      const s = image.settings as InheritedStyleSettings;
      if (s.mode === 'portrait') {
        return {
          ...s,
          mode: 'portrait',
          userPrompt:
            (s.userPrompt || '').trim() ||
            extractPortraitUserPrompt(image.prompt) ||
            '',
        };
      }
      return s;
    }

    const template = image.template || image.background;
    const outfit = image.outfit;
    const pose = image.pose;

    if (!template && !outfit && !pose) {
      return null;
    }

    if (template === 'musician') {
      return {
        mode: 'musician',
        dressId: outfit,
        poseId: pose,
      };
    }

    if (template === 'portrait') {
      return {
        mode: 'portrait',
        userPrompt:
          extractPortraitUserPrompt(image.prompt) ||
          image.prompt ||
          '',
      };
    }

    return {
      mode: 'standard',
      background: template,
      outfit,
      pose,
    };
  };

  const handleRegenerate = (imageId: string) => {
    const image = recentImages.find(img => img.id === imageId);
    if (!image) {
      alert('画像が見つかりません。');
      return;
    }

    const settings = buildSettingsFromRecentImage(image);
    if (!settings) {
      alert('再生成に必要な設定情報が見つかりません。この画像は古いバージョンで作成された可能性があります。');
      return;
    }

    inheritStyleAndNavigate({
      prompt: image.prompt || '',
      imageUrl: image.generatedImage || '',
      settings,
    });
  };

  const handleDownload = (imageUrl: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `headshot-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* パーソナライズド・ヒーロー */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {isAnonymous ? '無料版お試し中' : `${user?.displayName || user?.email?.split('@')[0]}さん、おかえりなさい！`}
            </h1>
            <p className="text-lg text-slate-600 mb-6">
              {isPaidUser ? (
                <>
                  {userPlan?.plan === 'standard' ? 'Standard プラン' : 
                   userPlan?.plan === 'pro' ? 'Pro プラン' : 'Pro Max プラン'}
                  <br />
                  残りクレジット: <span className="font-bold">{remainingCredits}</span> 回
                </>
              ) : (
                <>
                  無料プラン
                  <br />
                  残りクレジット: <span className="font-bold">{remainingCredits}</span> 回
                </>
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  console.log('🔍 「新しい写真を作る」ボタンがクリックされました');
                  console.log('🔍 onStartApp:', onStartApp);
                  console.log('🔍 onStartApp type:', typeof onStartApp);
                  if (onStartApp) {
                    onStartApp();
                  } else {
                    console.error('❌ onStartApp is undefined!');
                  }
                }}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
              >
                新しい写真を作る
              </button>
              <button
                onClick={() => {
                  window.location.href = '/musician';
                }}
                className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors shadow-lg"
              >
                音楽家版を作る 🎻
              </button>
              <button
                onClick={() => {
                  window.location.href = '/portrait';
                }}
                className="bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-800 transition-colors shadow-lg"
              >
                ポートレートモード
              </button>
              <button 
                onClick={onShowGallery}
                className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                ギャラリーを開く
              </button>
              <a
                href="/showcase.html"
                className="border-2 border-purple-600 text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
              >
                ショーケース
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 最近の生成 */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">最近の生成</h2>
          {recentImages.length > 0 ? (
            <div className="relative">
              {/* 横スクロールコンテナ */}
              <div className="overflow-x-auto pb-4 scrollbar-hide">
                <div className="flex gap-4 md:gap-6">
                  {recentImages.map((image) => (
                    <div key={image.id} className="flex-shrink-0 w-64 md:w-80 bg-white rounded-lg overflow-hidden shadow-sm border border-slate-200">
                      <div className="aspect-square relative">
                        <img
                          src={image.generatedImage}
                          alt="Generated headshot"
                          className="w-full h-full object-cover"
                        />
                    <div className="absolute top-2 left-2">
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                        高解像度DL済
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRegenerate(image.id)}
                      className="absolute top-3 right-3 z-[2] inline-flex items-center gap-1.5 rounded-full border-none bg-blue-600/95 px-[0.7rem] py-[0.45rem] text-xs font-bold leading-none text-white shadow-[0_8px_20px_rgba(37,99,235,0.35)] transition duration-150 hover:-translate-y-px hover:bg-blue-700 cursor-pointer"
                      title="このスタイルで作る"
                      aria-label="このスタイルで作る"
                    >
                      <StyleMarkIcon className="h-[0.95rem] w-[0.95rem] shrink-0" />
                      <span className="hidden sm:inline">このスタイルで</span>
                    </button>
                    <div className="absolute bottom-2 right-2 flex space-x-2">
                      <button
                        onClick={() => handleDownload(image.generatedImage)}
                        className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
                        title="ダウンロード"
                      >
                        <DownloadIcon className="w-4 h-4" />
                      </button>
                    </div>
                      </div>
                      <div className="p-4">
                        <p className="text-sm text-slate-600">
                          {new Date(image.createdAt).toLocaleDateString('ja-JP')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CameraIcon className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">まだ画像がありません</h3>
              <p className="text-slate-600 mb-4">最初の宣材写真を作成してみましょう</p>
              <button
                onClick={onStartApp}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                写真を作成
              </button>
            </div>
          )}
        </div>
      </section>

      {/* プラン＆クレジット表示 */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-6">
            <div className="flex items-center gap-3">
              <h2 id="plan-info-section" className="text-2xl font-bold text-slate-900">プラン情報</h2>
              {!userPlan && (
                <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  🔄 同期中...
                </span>
              )}
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-blue-900">
                  {!userPlan ? 'プラン情報を読み込み中...' :
                   userPlan?.plan === 'standard' ? 'Standard プラン (¥500/買い切り)' : 
                   userPlan?.plan === 'pro' ? 'Pro プラン (¥1,000/買い切り)' : 
                   userPlan?.plan === 'pro_max' ? 'Pro Max プラン (¥5,000/月)' : '無料プラン'}
                </h3>
                <p className="text-blue-700">
                  残りクレジット: <span className="font-bold">
                    {!userPlan ? '読み込み中...' : remainingCredits}
                  </span> 回
                </p>
              </div>
              <div className="text-right">
                <div className="w-16 h-16 relative">
                  <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="3"
                      strokeDasharray={`${((userPlan?.creditsRest || 0) / (
                      userPlan?.plan === 'standard' ? 10 :
                      userPlan?.plan === 'pro' ? 25 :
                      userPlan?.plan === 'pro_max' ? 200 : 3
                    )) * 100}, 100`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-900">
                      {Math.round(((userPlan?.creditsRest || 0) / (
                      userPlan?.plan === 'standard' ? 10 :
                      userPlan?.plan === 'pro' ? 25 :
                      userPlan?.plan === 'pro_max' ? 200 : 3
                    )) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Standard プランカード */}
              <div className="bg-white rounded-lg p-4 border border-blue-100">
                <h4 className="font-semibold text-slate-900 mb-2">Standard プラン</h4>
                <div className="text-2xl font-bold text-green-600 mb-3">¥500</div>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• 10枚の高解像度生成</li>
                  <li>• 全テンプレート利用可</li>
                  <li>• 買い切りプラン</li>
                </ul>
                <button
                  onClick={() => {
                    onPurchase('standard');
                  }}
                  disabled={userPlan?.plan === 'pro' || userPlan?.plan === 'pro_max'}
                  className={`mt-3 w-full py-2 px-4 rounded-lg transition-colors text-sm font-medium ${
                    userPlan?.plan === 'pro' || userPlan?.plan === 'pro_max'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {userPlan?.plan === 'pro' || userPlan?.plan === 'pro_max'
                    ? 'ダウングレード不可'
                    : 'Standard プランを購入'}
                </button>
              </div>
              
              {/* Pro プランカード */}
              <div className="bg-white rounded-lg p-4 border border-blue-100">
                <h4 className="font-semibold text-slate-900 mb-2">Pro プラン</h4>
                <div className="text-2xl font-bold text-blue-600 mb-3">¥1,000</div>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• 25枚の高解像度生成</li>
                  <li>• 全機能利用可</li>
                  <li>• 買い切りプラン</li>
                </ul>
                <button
                  onClick={() => {
                    onPurchase('pro');
                  }}
                  disabled={userPlan?.plan === 'pro' || userPlan?.plan === 'pro_max'}
                  className={`mt-3 w-full py-2 px-4 rounded-lg transition-colors text-sm font-medium ${
                    userPlan?.plan === 'pro' || userPlan?.plan === 'pro_max'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {userPlan?.plan === 'pro' 
                    ? '現在のプラン' 
                    : userPlan?.plan === 'pro_max'
                    ? 'ダウングレード不可'
                    : 'Pro プランを購入'}
                </button>
              </div>
              
              {/* Pro Max プランカード */}
              <div className="bg-white rounded-lg p-4 border border-blue-100">
                <h4 className="font-semibold text-slate-900 mb-2">Pro Max プラン</h4>
                <div className="text-2xl font-bold text-purple-600 mb-3">¥5,000<span className="text-sm font-normal">/月</span></div>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• 月200枚の高解像度生成</li>
                  <li>• 全機能利用可</li>
                  <li>• サブスクリプション</li>
                </ul>
                <button
                  onClick={() => onPurchase('pro_max')}
                  disabled={userPlan?.plan === 'pro_max'}
                  className={`mt-3 w-full py-2 px-4 rounded-lg transition-colors text-sm font-medium ${
                    userPlan?.plan === 'pro_max'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {userPlan?.plan === 'pro_max' 
                    ? '現在のプラン' 
                    : 'Pro Max プランを購入'}
                </button>
                
                {/* サブスクリプション管理ボタン（Pro Maxプラン契約者のみ） */}
                {userPlan?.plan === 'pro_max' && (
                  <button
                    onClick={async () => {
                      try {
                        const { redirectToCustomerPortal } = await import('../services/stripeService');
                        await redirectToCustomerPortal();
                      } catch (error: any) {
                        console.error('Portal error:', error);
                        alert(error.message || 'サブスクリプション管理ページへの遷移に失敗しました');
                      }
                    }}
                    className="mt-2 w-full py-2 px-4 rounded-lg transition-colors text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300"
                  >
                    サブスクリプション管理・解約
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* サポート・コミュニティ */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">サポート・コミュニティ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">お問い合わせ</h3>
              <p className="text-slate-600 mb-4">技術的な問題やご質問がございましたら、お気軽にお問い合わせください。</p>
              <button 
                onClick={() => window.location.href = 'mailto:support@profile-studio.online?subject=お問い合わせ&body=お問い合わせ内容をご記入ください。'}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                お問い合わせする
              </button>
            </div>
            <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-slate-900">更新ログ</h3>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  v1.8.0
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 font-medium">最新の更新</span>
                  <span className="text-xs text-slate-500">2026/08/21</span>
                </div>
                <p className="text-slate-600">• ショーケース・ギャラリーのスケルトン表示と高速化</p>
                <p className="text-slate-600">• 「このスタイルで」のプロンプト復元を修正</p>
                <p className="text-slate-600">• ショーケースのロゴからアプリへ戻れるように改善</p>
              </div>
              <button 
                onClick={() => setShowChangelog(true)}
                className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                すべての更新ログを見る →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 更新ログモーダル */}
      <ChangelogModal isOpen={showChangelog} onClose={() => setShowChangelog(false)} />
    </div>
  );
};

export const LandingPage: React.FC<LandingPageProps> = ({ onStartTrial, onShowTermsOfService, onShowPrivacyPolicy, onShowCommercialAct, onPurchase, onStartApp, onShowGallery }) => {
  const { user } = useAuth();

  // ログイン状態に応じて異なるLPを表示
  if (user) {
    return <LoggedInLandingPage onStartTrial={onStartTrial} onShowTermsOfService={onShowTermsOfService} onShowPrivacyPolicy={onShowPrivacyPolicy} onShowCommercialAct={onShowCommercialAct} onPurchase={onPurchase} onStartApp={onStartApp} onShowGallery={onShowGallery} />;
  }

  // 未ログイン時の従来のLP
  return (
    <div className="min-h-screen bg-white">
      <HeroSection onStartTrial={onStartTrial} />
      <UseCasesSection />
      <FlowSection />
      <GallerySection />
      <IndustryTemplates />
      <OutfitTemplates />
      <PricingSection onStartTrial={onStartTrial} onPurchase={onPurchase} />
      <TrustSection />
      <Footer onStartTrial={onStartTrial} onShowTermsOfService={onShowTermsOfService} onShowPrivacyPolicy={onShowPrivacyPolicy} onShowCommercialAct={onShowCommercialAct} />
    </div>
  );
};
